import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheContent {
  expiry: number;
  value: unknown;
}

/**
 * Global generic cache service
 * https://medium.com/@chandantechie/caching-service-in-angular-17-applications-9ebf5051f997
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private cache = new Map<string, CacheContent>();

  public get<T>(key: string): Observable<T> | undefined {
    const data = this.cache.get(key);
    if (!data) {
      return undefined;
    }

    const now = new Date().getTime();
    if (now > data.expiry) {
      this.cache.delete(key);
      return undefined;
    }

    return of(data.value as T);
  }

  public set<T>(
    key: string,
    value: T,
    ttl = 60_000 * 3600 /* default TTL 1 hour */,
  ): Observable<T> {
    const expiry = new Date().getTime() + ttl;
    this.cache.set(key, { expiry, value });
    return of(value);
  }

  public cacheObservable<T>(
    key: string,
    fallback: Observable<T>,
    ttl?: number,
  ): Observable<T> {
    const cached = this.get<T>(key);
    if (cached) {
      return cached;
    } else {
      return fallback.pipe(
        tap((value) => {
          this.set<T>(key, value, ttl);
        }),
      );
    }
  }

  public deleteCacheForKey(key: string): void {
    this.cache.delete(key);
  }
}
